import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7 } from "styled-components";
import React from "react";
import { gsap } from "gsap";
import { hasItems } from "~/features/app/utils";
import type { Mode, Theme } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Slider } from "~/features/ui/slider";
import { Progress } from "~/features/ui/slider/progress";
import { SectionHeader } from "./header";
import { Individual } from "./individual";
import { Pair } from "./pair";
import { Default } from "./default";
export type Props = {
  eyebrow?: React.ReactNode;
  headline?: string;
  text?: string;
  link?: React.ReactNode;
  children: React.ReactNode[];
  theme?: Theme;
  mode: Mode;
  mobileLayout?: "slider" | "grid";
  hasTopDivider?: boolean;
  isHighlighted?: boolean;
  filterBar?: React.ReactNode;
  filterKey?: string;
  perPage?: number;
  hasShowcaseCards?: boolean;
  headlineStart?: boolean;
};
const Section = _styled.section.withConfig({
  componentId: "sc-sf-1atbvx4-0"
})`
  ${{
  "overflow": "hidden"
}}

  .splide__track {
    overflow: visible !important;
  }
`;
var _StyledSection = _styled2(Section).withConfig({
  componentId: "sc-sf-1atbvx4-1"
})`${p => p.$_css}`;
export function AdaptiveSection(props: Props & React.ComponentPropsWithRef<"section">) {
  const {
    eyebrow,
    headline,
    text,
    children,
    mode,
    link,
    hasTopDivider,
    isHighlighted = false,
    // Also keeps `theme` out of `...rest`; it is reserved in styled-components.
    theme = "white",
    filterBar,
    filterKey,
    perPage,
    hasShowcaseCards,
    headlineStart,
    ...rest
  } = props;
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const mobileContainerRef = React.useRef<HTMLDivElement>(null);
  const mobileAnimRef = React.useRef<gsap.core.Tween | null>(null);
  const mobileFirstRender = React.useRef(true);
  const hasHeaderData = Boolean(eyebrow || headline || text || link || filterBar);
  React.useEffect(() => {
    if (mobileFirstRender.current) {
      mobileFirstRender.current = false;
      return;
    }
    mobileAnimRef.current?.kill();
    const container = mobileContainerRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      const slides = container.querySelectorAll(".splide__slide");
      if (slides.length === 0) return;
      mobileAnimRef.current = gsap.fromTo(slides, {
        y: 20,
        opacity: 0
      }, {
        y: 0,
        opacity: 1,
        duration: 0.5,
        ease: "cubic-bezier(0.125, 0.425, 0.27, 1)",
        stagger: 0.08
      });
    });
  }, [filterKey]);
  React.useEffect(() => {
    return () => {
      mobileAnimRef.current?.kill();
    };
  }, []);
  if (!hasItems(children) && !filterBar) {
    return null;
  }
  const items = children;
  return <_StyledSection data-theme={mode} {...rest} $_css={[hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, {
    "position": "relative",
    "color": "var(--text-primary)"
  },
  // Light mode only: these tokens are mode-aware, so applying the white branch in
  // dark would move existing dark sections from black-80 to black-100.
  isHighlighted || mode !== "dark" && theme !== "grey" ? {
    "backgroundColor": "var(--bg-primary)"
  } : {
    "backgroundColor": "var(--bg-secondary)"
  }]}>
      <_StyledWrap>
        {items.length === 1 && !filterBar && <Individual eyebrow={eyebrow} headline={headline} text={text} link={link} hasHeaderData={hasHeaderData}>
            {[items[0]]}
          </Individual>}
        {items.length === 2 && !filterBar && <Pair eyebrow={eyebrow} headline={headline} text={text} link={link} hasHeaderData={hasHeaderData}>
            {[items[0], items[1]]}
          </Pair>}
        {(items.length >= 3 || filterBar) && <Default hasHeaderData={hasHeaderData} eyebrow={eyebrow} headline={headline} text={text} link={link} filterBar={filterBar} filterKey={filterKey} perPage={perPage} hasShowcaseCards={hasShowcaseCards} headlineStart={headlineStart}>
            {items}
          </Default>}

        {/* Mobile slider */}
        <_StyledDiv $_css2={[{
        "@media (min-width: 1024px)": {
          "display": "none"
        }
      }]}>
          {hasHeaderData && <_StyledDiv2 $_css3={[{
          "width": "100%",
          "padding": "var(--spacing-xl)"
        }]}>
              <SectionHeader alignment="vertical" eyebrow={eyebrow} headline={headline} filterBar={filterBar} />
            </_StyledDiv2>}
          <_StyledDiv3 ref={mobileContainerRef} $_css4={[{
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingBottom": "var(--spacing-xl)",
          "paddingTop": "var(--spacing-md)"
        }]}>
            <Slider onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())} options={{
            fixedWidth: items.length === 1 ? "100%" : "95%",
            gap: 16
          }}>
              {items.map((child, idx) => <Slider.Slide key={idx}>{child}</Slider.Slide>)}
            </Slider>
          </_StyledDiv3>

          {items.length > 1 && <_StyledDiv4 $_css5={[{
          "paddingLeft": "var(--spacing-md)",
          "paddingRight": "var(--spacing-md)",
          "paddingTop": "var(--spacing-lg)",
          "paddingBottom": "var(--spacing-lg)"
        }]}>
              <Progress activeSlideIndex={activeSlideIndex} totalSlides={items.length} />
            </_StyledDiv4>}
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledWrap = _styled3(Wrap).withConfig({
  componentId: "sc-sf-1atbvx4-2"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-1atbvx4-3"
})`${p => p.$_css2}`;
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-1atbvx4-4"
})`${p => p.$_css3}`;
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-1atbvx4-5"
})`${p => p.$_css4}`;
var _StyledDiv4 = /*#__PURE__*/_styled7("div").withConfig({
  displayName: "adaptive-section___StyledDiv4",
  componentId: "sc-1dmo91w-0"
})(["", ""], p => p.$_css5);